"use client";

import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { type PasskeySessionResult, usePasskeyAuth } from "@/lib/use-passkey-auth";
import { ensureVisitorId } from "@/lib/use-visitor-auth";
import { useWidgetInstance } from "@/lib/widget-instance-context";

interface PasskeySignInButtonProps {
  /** Called with the verified visitor session once the ceremony
   *  succeeds — the shell adopts it (token already stamped into Apollo
   *  by the hook). */
  onSession?: (session: PasskeySessionResult) => void;
}

/**
 * Passkey (WebAuthn) sign-in for the auth gate. Two explicit actions —
 * a returning visitor (or a new device with a synced passkey) taps
 * "Sign in"; a first-timer taps "Set up". We don't auto-detect because
 * a silent "authenticate, fall back to register" flow double-prompts
 * the OS dialog, which reads as broken.
 *
 * Renders nothing when the browser has no WebAuthn — the gate then
 * falls through to its other method (Google) or the "host must
 * identify" hint. The live ceremony needs a real authenticator + an
 * origin in the integration's allow-list; verify in demo/real.
 */
export function PasskeySignInButton({ onSession }: PasskeySignInButtonProps) {
  const t = useTranslations("messages");
  const instance = useWidgetInstance();
  const publicKey = instance.publicKey;
  const visitorId = useMemo(
    () => ensureVisitorId(publicKey, instance.storage),
    [publicKey, instance.storage],
  );
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const { authenticate, register, status, supported } = usePasskeyAuth({
    visitorId,
    origin,
  });
  // Which action last failed — drives the guidance copy. A failed
  // *sign-in* almost always means "no passkey on this device yet" (the
  // browser's "no passkeys available"), so we steer first-timers to the
  // Set-up action rather than showing a dead-end "couldn't sign in".
  const [failedAction, setFailedAction] = useState<"signIn" | "register" | null>(null);

  if (!supported) return null;

  const run = async (
    kind: "signIn" | "register",
    action: () => Promise<PasskeySessionResult | null>,
  ) => {
    setFailedAction(null);
    const session = await action();
    if (session) onSession?.(session);
    else setFailedAction(kind);
  };

  const busy = status === "busy";

  return (
    <div className="flex w-full flex-col items-center gap-1.5">
      <button
        type="button"
        disabled={busy}
        onClick={() => run("signIn", authenticate)}
        className="flex w-full items-center justify-center gap-2 rounded-wx border border-wx-border bg-wx-bg-elevated px-4 py-2 text-sm font-medium text-wx-fg transition-colors hover:bg-wx-bg-elevated-2 disabled:opacity-60"
      >
        <PasskeyGlyph />
        {t("passkeySignIn")}
      </button>
      <button
        type="button"
        disabled={busy}
        // After a failed sign-in (no credential), promote Set-up so the
        // first-timer's obvious next tap creates a passkey.
        onClick={() => run("register", register)}
        className={
          failedAction === "signIn"
            ? "flex w-full items-center justify-center gap-2 rounded-wx border border-wx-primary bg-wx-bg-elevated px-4 py-2 text-sm font-medium text-wx-primary transition-colors hover:bg-wx-bg-elevated-2 disabled:opacity-60"
            : "text-xs text-wx-fg-muted underline-offset-2 hover:underline disabled:opacity-60"
        }
      >
        {t("passkeyRegister")}
      </button>
      {failedAction === "signIn" && (
        <p className="text-center text-xs text-wx-fg-subtle">{t("passkeyNoCredential")}</p>
      )}
      {failedAction === "register" && (
        <p className="text-center text-xs text-wx-danger">{t("passkeyError")}</p>
      )}
    </div>
  );
}

/** Minimal key/fingerprint mark — no external icon dep for the gate. */
function PasskeyGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="8" cy="9" r="4" />
      <path d="M10.5 12.5 21 23l-1.5-3 2-1-2-1.5L21 14l-4-1" />
    </svg>
  );
}
